/**
 * The sentences the meeting flow says (calls-v1 §5.3a). Every relaii client
 * says the same thing in the same situation, so the words live here once
 * rather than inline in whichever component shows them first.
 */

import type { CallDeviceKind } from '@/api/types'

/** A device as it is named inside a sentence. */
export function deviceLabel(kind: CallDeviceKind | null | undefined): string {
  switch (kind) {
    case 'web':
      return 'the browser'
    case 'ios':
      return 'your iPhone or iPad'
    case 'macos':
      return 'your Mac'
    case 'android':
      return 'your Android phone'
    default:
      // An older client takes a seat without saying what it is.
      return 'another device'
  }
}

/** What an unanswered invitation says once the ring window is over. */
export function ongoingNotice(room: string): string {
  return `Meeting in progress in ${room}`
}

/** The pre-join, when you are already in the call somewhere else. */
export function joinedElsewhereNotice(kind: CallDeviceKind | null | undefined): string {
  return `You're in this meeting on ${deviceLabel(kind)}. Joining here moves it here.`
}

/** The device the meeting just moved away from. */
export function replacedNotice(kind: CallDeviceKind | null | undefined): string {
  return `You joined this meeting on ${deviceLabel(kind)}.`
}

/** A peer that never connected, on its own tile. */
export function unreachableNotice(name: string): string {
  return `Can't reach ${name}`
}

export const MEETING_ENDED_NOTICE = 'This meeting has ended.'

/** Said by the session when the server has no seat left (Meetings.md §8). */
export const MEETING_FULL = 'The meeting is full'
export const MEETING_FULL_BODY = 'Eight are in. The way this meeting is built, there is no room for more.'

/** `Stops ringing in {n} s`, for the last 45 seconds of a ring and not before. */
export function ringCountdown(deadline: number | null, now: number): string | null {
  if (deadline === null) return null
  const seconds = Math.ceil((deadline - now) / 1000)
  return seconds > 0 && seconds <= 45 ? `Stops ringing in ${seconds} s` : null
}

/** `Video call · 12 min · with Anna and Bob`, for a meeting that ended under you. */
export function endedSummary(media: 'audio' | 'video', startedAt: string, endedAt: number, names: string[]): string {
  const minutes = Math.floor(Math.max(0, endedAt - new Date(startedAt).getTime()) / 60_000)
  const length = minutes < 1 ? '< 1 min' : `${minutes} min`
  const who =
    names.length === 0
      ? ''
      : names.length <= 2
        ? ` · with ${names.join(' and ')}`
        : ` · with ${names[0]} and ${names.length - 1} more`
  return `${media === 'video' ? 'Video call' : 'Call'} · ${length}${who}`
}
